import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  parseShareLink,
  parseConversationText,
  summarizeForImport,
  importCompanionWorld,
  ParsedConversation,
} from '../api';
import { useWorldsStore } from '../store';

// ============================================
// Types
// ============================================

type ImportStep = 'source' | 'type' | 'companion-config' | 'summarizing' | 'review-concept';
type ImportType = 'companion' | 'narrative';

interface ImportState {
  // Source step
  url: string;
  manualText: string;
  showManualInput: boolean;
  parsed: ParsedConversation | null;
  parseError: string;

  // Type selection
  importType: ImportType | null;

  // Companion config
  title: string;
  companionName: string;
  userPreferredName: string;
  companionRole: 'friend' | 'mentor' | 'coach' | 'confidant' | 'partner' | 'custom';
  companionMedium: 'text_message' | 'chat_app' | 'social_dm' | 'voice_call' | 'video_call' | 'in_person' | 'letters' | 'journal';

  // Summarization result
  generatedTitle: string;
  worldConcept: string;
  genre: string;
  tone: string;
}

interface ImportWorldWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onHandoffToCreate?: (data: { title: string; prompt: string }) => void;
}

// ============================================
// Provider Display Info
// ============================================

const PROVIDER_INFO: Record<string, { label: string; color: string }> = {
  claude: { label: 'Claude', color: 'text-orange-400' },
  chatgpt: { label: 'ChatGPT', color: 'text-green-400' },
  gemini: { label: 'Gemini', color: 'text-blue-400' },
  manual: { label: 'Manual', color: 'text-dark-300' },
};

// ============================================
// Component
// ============================================

export function ImportWorldWizard({ isOpen, onClose, onHandoffToCreate }: ImportWorldWizardProps) {
  const navigate = useNavigate();
  const addWorld = useWorldsStore(state => state.addWorld);

  const [step, setStep] = useState<ImportStep>('source');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [state, setState] = useState<ImportState>({
    url: '',
    manualText: '',
    showManualInput: false,
    parsed: null,
    parseError: '',
    importType: null,
    title: '',
    companionName: '',
    userPreferredName: '',
    companionRole: 'friend',
    companionMedium: 'text_message',
    generatedTitle: '',
    worldConcept: '',
    genre: '',
    tone: '',
  });

  const update = (partial: Partial<ImportState>) => {
    setState(prev => ({ ...prev, ...partial }));
  };

  // ---- Parse share link ----
  const handleParse = async () => {
    setIsLoading(true);
    setError('');
    update({ parseError: '' });

    try {
      const result = await parseShareLink(state.url.trim());
      update({ parsed: result });

      if (result.fallbackRequired) {
        update({ showManualInput: true, parseError: 'Could not extract messages from this link. Please paste the conversation manually.' });
      } else {
        // Auto-fill title from parsed data
        if (result.title) {
          update({ title: result.title, generatedTitle: result.title });
        }
        setStep('type');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to parse link';
      update({ parseError: message });
    } finally {
      setIsLoading(false);
    }
  };

  // ---- Parse manual text ----
  const handleParseText = async () => {
    setIsLoading(true);
    setError('');

    try {
      const result = await parseConversationText(state.manualText);
      update({ parsed: result, showManualInput: false });
      setStep('type');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to parse text';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  // ---- Summarize for narrative import ----
  const handleSummarize = async () => {
    if (!state.parsed) return;
    setStep('summarizing');
    setIsLoading(true);
    setError('');

    try {
      const result = await summarizeForImport(state.parsed.messages);
      update({
        generatedTitle: result.title,
        worldConcept: result.worldConcept,
        genre: result.genre,
        tone: result.tone,
      });
      setStep('review-concept');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Summarization failed';
      setError(message);
      setStep('type'); // Go back on error
    } finally {
      setIsLoading(false);
    }
  };

  // ---- Import companion ----
  const handleImportCompanion = async () => {
    if (!state.parsed) return;
    setIsLoading(true);
    setError('');

    try {
      const result = await importCompanionWorld({
        title: state.title || state.parsed.title || 'Imported Companion',
        companionName: state.companionName || undefined,
        userPreferredName: state.userPreferredName || undefined,
        role: state.companionRole,
        communicationMedium: state.companionMedium,
        importedMessages: state.parsed.messages,
        sourceProvider: state.parsed.provider,
      });

      addWorld(result.world);
      onClose();
      navigate(`/world/${result.world.id}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Import failed';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  // ---- Handoff to create wizard ----
  const handleContinueToBuilder = () => {
    if (onHandoffToCreate) {
      onHandoffToCreate({
        title: state.generatedTitle,
        prompt: state.worldConcept,
      });
    }
    onClose();
  };

  // ---- Navigation ----
  const goBack = () => {
    setError('');
    switch (step) {
      case 'type':
        setStep('source');
        break;
      case 'companion-config':
      case 'summarizing':
        setStep('type');
        break;
      case 'review-concept':
        setStep('type');
        break;
      default:
        break;
    }
  };

  const getStepTitle = (): string => {
    switch (step) {
      case 'source': return 'Import Conversation';
      case 'type': return 'Choose Import Type';
      case 'companion-config': return 'Configure Companion';
      case 'summarizing': return 'Analyzing Conversation...';
      case 'review-concept': return 'Review World Concept';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-600">
          <div>
            <h2 className="text-xl font-semibold text-dark-50">{getStepTitle()}</h2>
            <p className="text-dark-300 text-sm">
              {step === 'source' && 'Paste a share link from Claude, ChatGPT, or Gemini'}
              {step === 'type' && `${state.parsed?.messages.length} messages found`}
              {step === 'companion-config' && 'Set up your imported companion'}
              {step === 'summarizing' && 'AI is reading your conversation...'}
              {step === 'review-concept' && 'Edit the generated world concept'}
            </p>
          </div>
          <button onClick={onClose} className="text-dark-400 hover:text-dark-50">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {error && (
            <div className="mb-4 p-3 bg-red-900/30 border border-red-700 rounded text-red-300 text-sm">
              {error}
            </div>
          )}

          {step === 'source' && (
            <StepSource
              state={state}
              update={update}
              isLoading={isLoading}
              onParse={handleParse}
              onParseText={handleParseText}
            />
          )}

          {step === 'type' && state.parsed && (
            <StepTypeSelect
              parsed={state.parsed}
              selected={state.importType}
              onSelect={(type) => {
                update({ importType: type });
                if (type === 'companion') {
                  // Auto-suggest companion name from conversation
                  if (!state.companionName && state.parsed?.title) {
                    update({ companionName: state.parsed.title, title: state.parsed.title });
                  }
                  setStep('companion-config');
                } else {
                  handleSummarize();
                }
              }}
            />
          )}

          {step === 'companion-config' && (
            <StepCompanionConfig state={state} update={update} />
          )}

          {step === 'summarizing' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="spinner w-8 h-8 mb-4" />
              <p className="text-dark-200">Analyzing conversation and generating world concept...</p>
              <p className="text-dark-400 text-sm mt-2">This may take a moment</p>
            </div>
          )}

          {step === 'review-concept' && (
            <StepReviewConcept state={state} update={update} />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-dark-600 flex justify-between">
          <button
            onClick={step === 'source' ? onClose : goBack}
            className="px-4 py-2 bg-dark-700 text-dark-200 rounded hover:bg-dark-600 transition-colors"
            disabled={isLoading}
          >
            {step === 'source' ? 'Cancel' : 'Back'}
          </button>

          <div className="flex gap-3">
            {step === 'companion-config' && (
              <button
                onClick={handleImportCompanion}
                disabled={isLoading || !state.title}
                className="px-6 py-2 bg-primary-600 text-white rounded hover:bg-primary-500 transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Importing...' : 'Import Companion'}
              </button>
            )}

            {step === 'review-concept' && (
              <button
                onClick={handleContinueToBuilder}
                disabled={!state.worldConcept}
                className="px-6 py-2 bg-primary-600 text-white rounded hover:bg-primary-500 transition-colors disabled:opacity-50"
              >
                Continue to World Builder
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Step Components
// ============================================

function StepSource({
  state,
  update,
  isLoading,
  onParse,
  onParseText,
}: {
  state: ImportState;
  update: (p: Partial<ImportState>) => void;
  isLoading: boolean;
  onParse: () => void;
  onParseText: () => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-dark-200 mb-1">Share Link URL</label>
        <div className="flex gap-2">
          <input
            type="url"
            value={state.url}
            onChange={(e) => update({ url: e.target.value })}
            className="narrative-input flex-1 px-4 py-2"
            placeholder="https://claude.ai/share/... or https://chatgpt.com/share/..."
            disabled={isLoading}
          />
          <button
            onClick={onParse}
            disabled={isLoading || !state.url.trim()}
            className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-500 transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            {isLoading && !state.showManualInput ? 'Parsing...' : 'Parse'}
          </button>
        </div>
        <p className="text-dark-400 text-xs mt-1">
          Supports Claude.ai, ChatGPT, and Gemini share links
        </p>
      </div>

      {state.parseError && (
        <div className="p-3 bg-yellow-900/30 border border-yellow-700 rounded text-yellow-300 text-sm">
          {state.parseError}
        </div>
      )}

      {/* Manual paste fallback */}
      <div>
        <button
          onClick={() => update({ showManualInput: !state.showManualInput })}
          className="text-sm text-dark-300 hover:text-dark-100 flex items-center gap-1"
        >
          <svg className={`w-4 h-4 transition-transform ${state.showManualInput ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          Or paste conversation manually
        </button>

        {state.showManualInput && (
          <div className="mt-3 space-y-3">
            <textarea
              value={state.manualText}
              onChange={(e) => update({ manualText: e.target.value })}
              className="narrative-input w-full px-4 py-3 min-h-[200px]"
              placeholder={'Paste your conversation here...\n\nFormat:\nUser: Hello!\nAssistant: Hi there!\nUser: Tell me a story\nAssistant: Once upon a time...'}
              disabled={isLoading}
            />
            <button
              onClick={onParseText}
              disabled={isLoading || !state.manualText.trim()}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-500 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Parsing...' : 'Parse Text'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function StepTypeSelect({
  parsed,
  selected,
  onSelect,
}: {
  parsed: ParsedConversation;
  selected: ImportType | null;
  onSelect: (type: ImportType) => void;
}) {
  const providerInfo = PROVIDER_INFO[parsed.provider] || PROVIDER_INFO.manual;

  return (
    <div className="space-y-6">
      {/* Conversation summary */}
      <div className="p-4 bg-dark-700 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-sm font-medium ${providerInfo.color}`}>{providerInfo.label}</span>
          {parsed.title && <span className="text-dark-300 text-sm">- {parsed.title}</span>}
        </div>
        <div className="text-dark-200 text-sm">
          {parsed.messages.length} messages &middot; ~{Math.round(parsed.tokenEstimate / 1000)}k tokens
        </div>
        {/* Preview first few messages */}
        <div className="mt-3 space-y-2 max-h-40 overflow-y-auto">
          {parsed.messages.slice(0, 4).map((msg, i) => (
            <div key={i} className="text-xs">
              <span className={msg.role === 'user' ? 'text-blue-400' : 'text-green-400'}>
                {msg.role === 'user' ? 'User' : 'Assistant'}:
              </span>{' '}
              <span className="text-dark-300">
                {msg.content.length > 150 ? msg.content.slice(0, 150) + '...' : msg.content}
              </span>
            </div>
          ))}
          {parsed.messages.length > 4 && (
            <div className="text-xs text-dark-400">...and {parsed.messages.length - 4} more messages</div>
          )}
        </div>
      </div>

      {/* Import type selection */}
      <div>
        <h3 className="text-lg font-medium text-dark-50 mb-3">How would you like to import this?</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => onSelect('companion')}
            className="p-4 rounded-lg text-left bg-dark-700 border border-dark-600 hover:border-primary-500 transition-colors"
          >
            <div className="text-lg font-medium text-dark-50 mb-1">Companion</div>
            <p className="text-dark-300 text-sm">
              Import as an AI companion with full chat history. The conversation will be preserved and the companion will learn from it.
            </p>
          </button>
          <button
            onClick={() => onSelect('narrative')}
            className="p-4 rounded-lg text-left bg-dark-700 border border-dark-600 hover:border-primary-500 transition-colors"
          >
            <div className="text-lg font-medium text-dark-50 mb-1">Reality Simulation</div>
            <p className="text-dark-300 text-sm">
              Convert the conversation into a narrative world concept. AI will analyze the themes, characters, and setting to create a playable world.
            </p>
          </button>
        </div>
      </div>
    </div>
  );
}

function StepCompanionConfig({
  state,
  update,
}: {
  state: ImportState;
  update: (p: Partial<ImportState>) => void;
}) {
  const roleOptions: ImportState['companionRole'][] = ['friend', 'mentor', 'coach', 'confidant', 'partner', 'custom'];

  const mediumOptions: { value: ImportState['companionMedium']; label: string; description: string }[] = [
    { value: 'text_message', label: 'Text Messages', description: 'SMS-style texting' },
    { value: 'chat_app', label: 'Chat App', description: 'Messenger/WhatsApp vibe' },
    { value: 'social_dm', label: 'Social DM', description: 'Twitter/Instagram DMs' },
    { value: 'voice_call', label: 'Phone Call', description: 'Voice-only dialogue' },
    { value: 'video_call', label: 'Video Call', description: 'FaceTime/Zoom style' },
    { value: 'in_person', label: 'In Person', description: 'Face to face' },
    { value: 'letters', label: 'Letters', description: 'Written correspondence' },
    { value: 'journal', label: 'Journal', description: 'Companion as diary' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-dark-200 mb-1">World Title *</label>
        <input
          type="text"
          value={state.title}
          onChange={(e) => update({ title: e.target.value })}
          className="narrative-input w-full px-4 py-2"
          placeholder="My Companion"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-dark-200 mb-1">Companion Name</label>
        <input
          type="text"
          value={state.companionName}
          onChange={(e) => update({ companionName: e.target.value })}
          className="narrative-input w-full px-4 py-2"
          placeholder="Defaults to world title"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-dark-200 mb-1">What should your companion call you?</label>
        <input
          type="text"
          value={state.userPreferredName}
          onChange={(e) => update({ userPreferredName: e.target.value })}
          className="narrative-input w-full px-4 py-2"
          placeholder="Defaults to your profile name"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-dark-200 mb-2">Role</label>
        <div className="flex flex-wrap gap-2">
          {roleOptions.map(role => (
            <button
              key={role}
              onClick={() => update({ companionRole: role })}
              className={`px-3 py-1.5 rounded text-sm capitalize ${
                state.companionRole === role
                  ? 'bg-primary-600 text-white'
                  : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
              }`}
            >
              {role}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-dark-200 mb-2">Communication Medium</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {mediumOptions.map(option => (
            <button
              key={option.value}
              onClick={() => update({ companionMedium: option.value })}
              className={`p-3 rounded text-left ${
                state.companionMedium === option.value
                  ? 'bg-primary-600/20 border border-primary-500 text-primary-200'
                  : 'bg-dark-700 border border-dark-600 text-dark-300 hover:bg-dark-600'
              }`}
            >
              <div className="text-sm font-medium">{option.label}</div>
              <div className="text-xs opacity-70 mt-1">{option.description}</div>
            </button>
          ))}
        </div>
      </div>

      {state.parsed && (
        <div className="p-3 bg-dark-700/50 rounded text-dark-300 text-sm">
          {state.parsed.messages.length} messages will be imported into this companion's history.
        </div>
      )}
    </div>
  );
}

function StepReviewConcept({
  state,
  update,
}: {
  state: ImportState;
  update: (p: Partial<ImportState>) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <p className="text-dark-300 text-sm mb-4">
          Review and edit the generated world concept before continuing to the World Builder.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-dark-200 mb-1">Title</label>
        <input
          type="text"
          value={state.generatedTitle}
          onChange={(e) => update({ generatedTitle: e.target.value })}
          className="narrative-input w-full px-4 py-2"
          placeholder="World title"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-dark-200 mb-1">Genre</label>
          <input
            type="text"
            value={state.genre}
            onChange={(e) => update({ genre: e.target.value })}
            className="narrative-input w-full px-4 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-dark-200 mb-1">Tone</label>
          <input
            type="text"
            value={state.tone}
            onChange={(e) => update({ tone: e.target.value })}
            className="narrative-input w-full px-4 py-2"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-dark-200 mb-1">World Concept</label>
        <textarea
          value={state.worldConcept}
          onChange={(e) => update({ worldConcept: e.target.value })}
          className="narrative-input w-full px-4 py-3 min-h-[200px]"
          placeholder="World concept..."
        />
        <p className="text-dark-400 text-xs mt-1">
          This will be used as the prompt for the World Builder scaffolding.
        </p>
      </div>
    </div>
  );
}
